"use client";

import { useEffect, useState } from "react";
import Confetti from "react-confetti";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";

interface PaymentSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  transactionId: string;
}

export function PaymentSuccessModal({
  isOpen,
  onClose,
  amount,
  transactionId,
}: PaymentSuccessModalProps) {
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Set window size for confetti
    if (typeof window !== "undefined") {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });

      const handleResize = () => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  useEffect(() => {
    // Stop confetti after 5 seconds
    if (isOpen) {
      setShowConfetti(true);
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  return (
    <>
      {showConfetti && isOpen && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.3}
        />
      )}

      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md relative overflow-hidden bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-green-500/10 dark:via-card dark:to-background backdrop-blur-xl border border-green-200 dark:border-green-500/30 shadow-2xl">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-[0.08] dark:opacity-[0.05]">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2310b981' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            />
          </div>

          {/* Floating Elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-200/60 to-white/90 dark:from-green-500/10 dark:to-green-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-28 h-28 bg-gradient-to-br from-white/95 to-green-200/40 dark:from-green-500/8 dark:to-green-500/4 rounded-full blur-3xl" />

          <div className="relative z-10 flex flex-col items-center text-center p-6 space-y-6">
            {/* Success Icon */}
            <div className="relative">
              <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping" />
              <div className="relative p-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full">
                <CheckCircle
                  className="h-16 w-16 text-white"
                  strokeWidth={2.5}
                />
              </div>
            </div>

            {/* Success Message */}
            <div className="space-y-3">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-foreground">
                Payment Successful! 🎉
              </h2>
              <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                ₹{amount.toFixed(2)}
              </p>
              <p className="text-sm text-slate-600 dark:text-muted-foreground max-w-sm">
                Your payment has been confirmed and the amount will be reflected
                in your wallet shortly.
              </p>
            </div>

            {/* Transaction Details */}
            <div className="w-full bg-white/50 dark:bg-card/30 backdrop-blur-sm rounded-xl border border-green-200 dark:border-green-500/30 p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 dark:text-muted-foreground">
                  Transaction ID
                </span>
                <span className="font-mono text-xs text-slate-900 dark:text-foreground bg-green-100 dark:bg-green-500/20 px-2 py-1 rounded">
                  {transactionId}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 dark:text-muted-foreground">
                  Status
                </span>
                <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-medium">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Processing
                </span>
              </div>
            </div>

            {/* Action Button */}
            <Button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg"
            >
              Continue
            </Button>

            <p className="text-xs text-slate-500 dark:text-muted-foreground">
              You will receive a confirmation email shortly
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
